import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { GlobalSettings } from '../../../../shared/components/global-settings-wrapper';
import { RootState } from '../../../../store/root-reducer';
import { RequestStatus } from '../../../../shared/enums/request-status';
import { getIsRequestPending } from '../../../../shared/utils';
import { constants } from '../../../../shared/enums/constants';
import toaster from '../../../../shared/toaster';
import { CallOutcomeContentTabs } from './enums';
import { CallOutcome as CallOutcomeType, CreateUpdateCallOutcomeModalData } from './types';
import {
  getCustomCallOutcomesRequest,
  getDefaultCallOutcomesRequest,
  getAllCallOutcomesRequest,
  createCallOutcomeRequest,
  updateCallOutcomeRequest,
  deleteCallOutcomeRequest,
} from './extra-actions';
import { resetCallOutcomeData } from './call-outcome-slice';
import CallOutcomeHeader from './components/call-outcome-header';
import CallOutcomeContent from './components/call-outcome-content';
import CreateUpdateCallOutcomeModal from './components/create-update-call-outcome-modal';
import DeleteCallOutcomeModal from './components/delete-call-outcome-modal';

const CallOutcome: React.FC = () => {
  const dispatch = useDispatch();

  // Redux state
  const {
    customOutcomes,
    defaultOutcomes,
    allOutcomes,
    isLoading,
    customOutcomePagination,
    defaultOutcomePagination,
    getCustomCallOutcomesRequest: getCustomReqState,
    getDefaultCallOutcomesRequest: getDefaultReqState,
    getAllCallOutcomesRequest: getAllReqState,
    createCallOutcomeRequest: createReqState,
    updateCallOutcomeRequest: updateReqState,
    deleteCallOutcomeRequest: deleteReqState,
  } = useSelector((state: RootState) => state.callOutcome);

  // Local UI state
  const [activeTabKey, setActiveTabKey] = useState<string>(
    CallOutcomeContentTabs.CustomOutcomes,
  );
  const [actionOutcome, setActionOutcome] = useState<CallOutcomeType>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteNewOutcomeId, setDeleteNewOutcomeId] = useState<number>(1);

  // Refs to track previous request statuses
  const prevCreateStatus = useRef(createReqState.status);
  const prevUpdateStatus = useRef(updateReqState.status);
  const prevDeleteStatus = useRef(deleteReqState.status);

  // Loading bar
  useEffect(() => {
    if (isLoading) {
      dispatch(showLoading());
    } else {
      dispatch(hideLoading());
    }
  }, [isLoading, dispatch]);

  // Fetch outcomes on mount and tab change
  const fetchOutcomes = useCallback(
    (forceRefetch = false) => {
      const { options: cOpts } = customOutcomePagination;
      const { options: dOpts } = defaultOutcomePagination;

      if (
        activeTabKey === CallOutcomeContentTabs.CustomOutcomes &&
        (customOutcomes.length <= 0 || forceRefetch)
      ) {
        dispatch(
          getCustomCallOutcomesRequest({
            limit: cOpts.limit,
            page: cOpts.page,
            search: cOpts.search,
          }),
        );
      } else if (
        activeTabKey === CallOutcomeContentTabs.SystemOutcomes &&
        (defaultOutcomes.length <= 0 || forceRefetch)
      ) {
        dispatch(
          getDefaultCallOutcomesRequest({
            limit: dOpts.limit,
            page: dOpts.page,
            search: dOpts.search,
          }),
        );
      }
    },
    [
      activeTabKey,
      customOutcomes.length,
      defaultOutcomes.length,
      customOutcomePagination,
      defaultOutcomePagination,
      dispatch,
    ],
  );

  useEffect(() => {
    fetchOutcomes();
  }, [activeTabKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(
    () => () => {
      dispatch(resetCallOutcomeData());
    },
    [dispatch],
  );

  // Create outcome response handling
  useEffect(() => {
    if (createReqState.status !== prevCreateStatus.current) {
      if (createReqState.status === RequestStatus.Succeeded) {
        toaster.success(createReqState.message);
        setShowCreateModal(false);
        setActionOutcome(null);
        dispatch(
          getCustomCallOutcomesRequest({
            limit: customOutcomePagination.options.limit,
            page: customOutcomePagination.options.page,
          }),
        );
      }
      if (
        createReqState.status === RequestStatus.Failed &&
        createReqState.error
      ) {
        toaster.error(createReqState.error.message);
      }
      prevCreateStatus.current = createReqState.status;
    }
  }, [createReqState, customOutcomePagination.options, dispatch]);

  // Update outcome response handling
  useEffect(() => {
    if (updateReqState.status !== prevUpdateStatus.current) {
      if (updateReqState.status === RequestStatus.Succeeded) {
        const isDefault = actionOutcome?.isDefault === 1;
        toaster.success(
          isDefault
            ? 'System outcome updated successfully'
            : 'Custom outcome updated successfully',
        );
        setShowUpdateModal(false);
        setActionOutcome(null);
        fetchOutcomes(true);
      }
      if (
        updateReqState.status === RequestStatus.Failed &&
        updateReqState.error
      ) {
        toaster.error(updateReqState.error.message);
      }
      prevUpdateStatus.current = updateReqState.status;
    }
  }, [updateReqState, actionOutcome, fetchOutcomes]);

  // Delete outcome response handling
  useEffect(() => {
    if (deleteReqState.status !== prevDeleteStatus.current) {
      if (deleteReqState.status === RequestStatus.Succeeded) {
        toaster.success(deleteReqState.message);
        setShowDeleteModal(false);
        setActionOutcome(null);
        setDeleteNewOutcomeId(1);
        dispatch(
          getCustomCallOutcomesRequest({
            limit: constants.DEFAULT_PAGE_SIZE,
            page: 1,
          }),
        );
      }
      if (
        deleteReqState.status === RequestStatus.Failed &&
        deleteReqState.error
      ) {
        toaster.error(deleteReqState.error.message);
      }
      prevDeleteStatus.current = deleteReqState.status;
    }
  }, [deleteReqState, dispatch]);

  // Pagination data for child components
  const customPagination = {
    ...customOutcomePagination,
    pagesCount: null,
    options: {
      ...customOutcomePagination.options,
      totalElements: customOutcomePagination.count,
    },
  };

  const defaultPagination = {
    ...defaultOutcomePagination,
    pagesCount: null,
    options: {
      ...defaultOutcomePagination.options,
      totalElements: defaultOutcomePagination.count,
    },
  };

  // Handlers
  const sendGetCustomOutcomeRequest = useCallback(
    (params) => {
      dispatch(getCustomCallOutcomesRequest(params));
    },
    [dispatch],
  );

  const sendGetDefaultOutcomeRequest = useCallback(
    (params) => {
      dispatch(getDefaultCallOutcomesRequest(params));
    },
    [dispatch],
  );

  const onCreateHandler = useCallback(() => {
    setActionOutcome(null);
    setShowCreateModal(true);
  }, []);

  const onUpdateHandler = useCallback((outcome: CallOutcomeType) => {
    setActionOutcome(outcome);
    setShowUpdateModal(true);
  }, []);

  const onDeleteHandler = useCallback((outcome: CallOutcomeType) => {
    setActionOutcome(outcome);
    setShowDeleteModal(true);
  }, []);

  const hideCreateModal = useCallback(() => {
    setActionOutcome(null);
    setShowCreateModal(false);
  }, []);

  const hideUpdateModal = useCallback(() => {
    setActionOutcome(null);
    setShowUpdateModal(false);
  }, []);

  const hideDeleteModal = useCallback(() => {
    setActionOutcome(null);
    setShowDeleteModal(false);
    setDeleteNewOutcomeId(1);
  }, []);

  const onCreateSubmit = useCallback(
    (payload: CreateUpdateCallOutcomeModalData) => {
      dispatch(
        createCallOutcomeRequest({
          name: payload.label,
          sentiment: payload.sentiment,
        }),
      );
    },
    [dispatch],
  );

  const onUpdateSubmit = useCallback(
    (payload: CreateUpdateCallOutcomeModalData) => {
      if (actionOutcome) {
        dispatch(
          updateCallOutcomeRequest({
            callOutcomeId: actionOutcome.id,
            name: payload.label,
            sentiment: payload.sentiment,
          }),
        );
      }
    },
    [dispatch, actionOutcome],
  );

  const onDeleteSubmit = useCallback(() => {
    if (actionOutcome) {
      dispatch(
        deleteCallOutcomeRequest({
          callOutcomeId: actionOutcome.id,
          replacementCallOutcomeId: deleteNewOutcomeId,
        }),
      );
    }
  }, [dispatch, actionOutcome, deleteNewOutcomeId]);

  const sendGetAllOutcomesRequest = useCallback(() => {
    dispatch(getAllCallOutcomesRequest());
  }, [dispatch]);

  const isRequestPending =
    getIsRequestPending(getCustomReqState.status) ||
    getIsRequestPending(getDefaultReqState.status);

  return (
    <GlobalSettings.Content className="call-outcomes-container">
      <div className="call-outcome-grid bs-mb-30">
        <CallOutcomeHeader headerName="Call Outcome" />
        <CallOutcomeContent
          isRequestPending={isRequestPending}
          sendGetCustomOutcomeRequest={sendGetCustomOutcomeRequest}
          sendGetDefaultOutcomeRequest={sendGetDefaultOutcomeRequest}
          customOutcomes={customOutcomes}
          defaultOutcomes={defaultOutcomes}
          customOutcomePagination={customPagination}
          defaultOutcomePagination={defaultPagination}
          onCreateHandler={onCreateHandler}
          onUpdateHandler={onUpdateHandler}
          onDeleteHandler={onDeleteHandler}
          activeTabKey={activeTabKey}
          setActiveTabKey={setActiveTabKey}
        />
      </div>
      {showCreateModal && (
        <CreateUpdateCallOutcomeModal
          show={showCreateModal}
          modalHeaderTitle="Add Custom Outcome"
          onClose={hideCreateModal}
          onSave={onCreateSubmit}
          isRequestPending={createReqState.status === RequestStatus.Pending}
        />
      )}
      {showUpdateModal && (
        <CreateUpdateCallOutcomeModal
          show={showUpdateModal}
          modalHeaderTitle={
            actionOutcome?.isDefault === 1
              ? 'Update System Outcome'
              : 'Update Custom Outcome'
          }
          onClose={hideUpdateModal}
          onSave={onUpdateSubmit}
          isRequestPending={updateReqState.status === RequestStatus.Pending}
          customOutcome={actionOutcome}
        />
      )}
      {showDeleteModal && (
        <DeleteCallOutcomeModal
          show={showDeleteModal}
          onClose={hideDeleteModal}
          onSubmit={onDeleteSubmit}
          isRequestPending={deleteReqState.status === RequestStatus.Pending}
          onOutcomeIdUpdate={setDeleteNewOutcomeId}
          options={allOutcomes}
          sendAllOutcomesRequest={sendGetAllOutcomesRequest}
          getAllOutcomesRequestStatus={getAllReqState.status}
          actionCustomOutcome={actionOutcome}
        />
      )}
    </GlobalSettings.Content>
  );
};

export default CallOutcome;
